import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import type { ProcessingStatus } from "../components/customer-requests/processing/processingSteps"

type TranslateFn = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

export const QUESTIONNAIRES_BASE = "/material-compliance/questionnaires"
const CUSTOMERS_BASE = "/material-compliance/customers"

export type QuestionnaireStatus =
  | "extracting"
  | "needs_confirm"
  | "stored_pending"
  | "processed"
  | "extraction_failed"

// ─── API DTOs ───────────────────────────────────────────────────────────────
// The apiClient interceptor converts response keys snake_case → camelCase, so
// these mirror the backend schemas with camelCase field names.

export interface CustomerOut {
  id: string
  name: string
  email: string | null
  preferredResponseFormat: string | null
  additionalAttributes: Record<string, unknown> | null
  declarationFreshnessDays: number | null
  concentrationThresholdPct: number | null
  createdAt: string | Date | null
  updatedAt: string | Date | null
}

interface DetectedMetadataDto {
  customer: {
    id: string
    name: string
    confidence: number
    matchSource: "content" | "filename"
  } | null
  frameworks: { name: string; confidence: number }[]
  articleCodes: string[]
  dueDate: string | null
  documentType: string | null
}

interface ActivityEntryDto {
  timestamp: string | null
  actor: string
  action: string
  field: string | null
  oldValue: string | null
  newValue: string | null
  detail: string | null
  detailKey: string | null
  detailParams: Record<string, string>
}

interface QuestionnaireListItemDto {
  id: string
  requestId: string
  customerId: string | null
  customerName: string
  fileName: string | null
  frameworks: string[]
  /** One canonical `mc_framework_codes` code per `frameworks` entry, same
   *  order/length — see `Declaration.frameworkCodes` (api/types.ts). */
  frameworkCodes?: string[]
  /** The regulations the request asks as FRAMEWORK_ORDER keys (`REACH_SVHC`): the
   *  confirmed set, else the keys the detected wording names. */
  regulationKeys?: string[]
  /** A person confirmed or edited `regulationKeys`; false while they are read
   *  from the detected wording. */
  regulationKeysConfirmed?: boolean
  /** Detected spellings with no key (FMD, SCIP), shown as such, never guessed. */
  unmappedFrameworks?: string[]
  articlesCount: number
  customerDueDate: string | null
  status: QuestionnaireStatus
  processingStatus: ProcessingStatus
  source: string
  receivedAt: string | null
  createdBy: string | null
}

interface QuestionnaireDetailDto extends QuestionnaireListItemDto {
  /** An earlier LIVE upload of the exact same bytes. Set only on the upload
   *  response — a hint, never a refusal: the same questionnaire can legitimately
   *  arrive twice. The id and date come with it because the hint is rendered on
   *  the confirm page, which the user reaches after the upload navigates away. */
  duplicateOfRequestId?: string | null
  duplicateOfId?: string | null
  duplicateOfUploadedAt?: string | null
  fileKey: string | null
  fileUrl: string | null
  documentType: string | null
  notes: string | null
  articleCodes: string[]
  createdAt: string | Date | null
  updatedAt: string | Date | null
  detectedMetadata: DetectedMetadataDto | null
  activity: ActivityEntryDto[]
  /** Provenance of customerDueDate: "extracted" (AI proposal, unconfirmed) |
   *  "confirmed" (a human submitted/edited it) | null. F22 — mirrors the
   *  backend's `DueDateSource` Literal (mc_constants.py) verbatim, not just a
   *  bare `string`, and the same optional/nullable shape as `validUntilSource?`
   *  (types.ts): an older cached payload or a response shape that never
   *  populated this field omits the key entirely, and the mapper below
   *  already reads it with `?? null`. */
  customerDueDateSource?: "extracted" | "confirmed" | null
}

interface QuestionnairesPageDto {
  items: QuestionnaireListItemDto[]
  total: number
  page: number
  pageSize: number
}

// ─── View models (consumed by the components) ─────────────────────────────────

export interface ActivityEntry {
  timestamp: string | null
  actor: string
  action: string
  field?: string | null
  oldValue?: string | null
  newValue?: string | null
  detail?: string | null
  detailKey?: string | null
  detailParams?: Record<string, string>
}

export interface QuestionnaireRow {
  id: string // UUID — used for fetch + navigation
  requestId: string // "QRE-2026-0118" — displayed
  customer: string
  doc: string | null
  docType: string | null
  articles: number
  articleCodes: string[]
  received: string
  due: string
  createdAt: string
  status: QuestionnaireStatus
  processingStatus: ProcessingStatus
  frameworks: string[]
  /** One canonical `mc_framework_codes` code per `frameworks` entry, same
   *  order/length — see `Declaration.frameworkCodes` (api/types.ts). */
  frameworkCodes: string[]
  regulationKeys: string[]
  regulationKeysConfirmed: boolean
  unmappedFrameworks: string[]
  source: string
  notes: string | null
  createdBy: string
  activity: ActivityEntry[]
}

export interface QuestionnaireIntake {
  id: string // UUID of the upload draft (passed to store + delete)
  requestId: string
  status: QuestionnaireStatus // async extraction: extracting → needs_confirm | extraction_failed
  customer: string
  customerId: string | null
  contact: string
  doc: string
  docSize: string
  docType: string
  fileKey: string | null
  fileUrl: string | null
  fileName: string | null
  received: string
  due: string
  /** Provenance of `due`: "extracted" (AI proposal, unconfirmed) | "confirmed"
   *  (a human submitted/edited it) | null. Drives the confirm-form hint. */
  dueSource: "extracted" | "confirmed" | null
  /** As on the list row: what the request asks, as keys, and the detected
   *  spellings with no key. */
  regulationKeys: string[]
  unmappedFrameworks: string[]
  detected: {
    customer: { name: string; confidence: number } | null
    template: string
    frameworks: { name: string; conf: number }[]
    articleCount: number
    articleCodes: string[]
    questionsPerArticle: number
    dueDate: string
  }
}

export interface QuestionnaireEditable {
  customer: string
  customerId: string | null
  frameworks: string[]
  /** The regulations the reviewer confirms, as FRAMEWORK_ORDER keys. */
  regulationKeys: string[]
  due: string
  articleCodes: string[]
  notes: string
}

export interface QuestionnaireStats {
  received: number
  pending: number
  stored: number
  processed: number
  extracting: number
  failed: number
}

export interface QuestionnaireListParams {
  status?: QuestionnaireStatus | ""
  customerId?: string
  productId?: string
  /** A regulation key (`ROHS`): requests asking it, confirmed or detected. */
  regulation?: string
  fromDate?: string
  toDate?: string
  overdue?: boolean
  /** Dashboard drill-down bucket (answering | waiting | gaps | overdue) —
   *  forwarded verbatim to the backend, which validates it (422 on an
   *  unknown value). See mc_questionnaires.list_questionnaires. */
  bucket?: string
  search?: string
  sortBy?: string
  sortDir?: "asc" | "desc"
  page?: number
  pageSize?: number
}

export interface QuestionnairesPage {
  items: QuestionnaireRow[]
  total: number
  page: number
  pageSize: number
}

interface StoreQuestionnaireInput {
  /** UUID of the upload draft to confirm in place (transitioned to stored_pending). */
  draftId?: string
  customerId: string
  fileKey?: string | null
  fileName?: string | null
  documentType?: string | null
  customerDueDate?: string | null
  source?: string
  frameworks?: string[]
  /** Omitted: the backend reads the keys the detected wording names. */
  regulationKeys?: string[]
  articleCodes?: string[]
  notes?: string | null
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

/** createdAt/updatedAt arrive as Date objects (per the client interceptor). */
function asDateString(v: string | Date | null | undefined): string {
  if (!v) return ""
  return v instanceof Date ? v.toISOString() : v
}

function toDateOnly(v: string | Date | null | undefined): string {
  const s = asDateString(v)
  return s ? s.slice(0, 10) : ""
}

function mapListItemToRow(d: QuestionnaireListItemDto): QuestionnaireRow {
  return {
    id: d.id,
    requestId: d.requestId,
    customer: d.customerName,
    doc: d.fileName,
    docType: null,
    articles: d.articlesCount ?? 0,
    articleCodes: [],
    received: toDateOnly(d.receivedAt),
    due: toDateOnly(d.customerDueDate),
    createdAt: "",
    status: d.status,
    processingStatus: d.processingStatus,
    frameworks: d.frameworks ?? [],
    frameworkCodes: d.frameworkCodes ?? [],
    regulationKeys: d.regulationKeys ?? [],
    regulationKeysConfirmed: d.regulationKeysConfirmed ?? false,
    unmappedFrameworks: d.unmappedFrameworks ?? [],
    source: d.source,
    notes: null,
    createdBy: d.createdBy ?? "",
    activity: [],
  }
}

function mapDetailToRow(d: QuestionnaireDetailDto): QuestionnaireRow {
  return {
    id: d.id,
    requestId: d.requestId,
    customer: d.customerName,
    doc: d.fileName,
    docType: d.documentType ?? null,
    articles: d.articlesCount ?? d.articleCodes?.length ?? 0,
    articleCodes: d.articleCodes ?? [],
    received: toDateOnly(d.receivedAt),
    due: toDateOnly(d.customerDueDate),
    createdAt: asDateString(d.createdAt),
    status: d.status,
    processingStatus: d.processingStatus,
    frameworks: d.frameworks ?? [],
    frameworkCodes: d.frameworkCodes ?? [],
    regulationKeys: d.regulationKeys ?? [],
    regulationKeysConfirmed: d.regulationKeysConfirmed ?? false,
    unmappedFrameworks: d.unmappedFrameworks ?? [],
    source: d.source,
    notes: d.notes ?? null,
    createdBy: d.createdBy ?? "",
    activity: (d.activity ?? []).map((a) => ({ ...a })),
  }
}

function mapDetailToIntake(d: QuestionnaireDetailDto): QuestionnaireIntake {
  const dm = d.detectedMetadata
  return {
    id: d.id,
    requestId: d.requestId,
    status: d.status,
    customer: d.customerName,
    customerId: d.customerId ?? dm?.customer?.id ?? null,
    contact: "",
    doc: d.fileName ?? "",
    docSize: "",
    docType: d.documentType ?? "",
    fileKey: d.fileKey ?? null,
    fileUrl: d.fileUrl ?? null,
    fileName: d.fileName ?? null,
    received: toDateOnly(d.receivedAt),
    due: toDateOnly(d.customerDueDate),
    dueSource: d.customerDueDateSource ?? null,
    regulationKeys: d.regulationKeys ?? [],
    unmappedFrameworks: d.unmappedFrameworks ?? [],
    detected: {
      customer: dm?.customer
        ? { name: dm.customer.name, confidence: dm.customer.confidence }
        : null,
      template: dm?.documentType ?? d.documentType ?? "",
      frameworks: (dm?.frameworks ?? []).map((f) => ({
        name: f.name,
        conf: f.confidence,
      })),
      articleCount: dm?.articleCodes?.length ?? d.articlesCount ?? 0,
      articleCodes: dm?.articleCodes ?? d.articleCodes ?? [],
      questionsPerArticle: 0,
      dueDate: toDateOnly(dm?.dueDate ?? d.customerDueDate),
    },
  }
}

// ─── API calls ────────────────────────────────────────────────────────────────

const customerRequestsApiCalls = {
  listQuestionnaires: async (
    params: QuestionnaireListParams,
  ): Promise<QuestionnairesPage> => {
    const data = await apiClientV2
      .get(QUESTIONNAIRES_BASE, {
        params: {
          status: params.status || undefined,
          customer_id: params.customerId || undefined,
          product_id: params.productId || undefined,
          regulation: params.regulation || undefined,
          from_date: params.fromDate || undefined,
          to_date: params.toDate || undefined,
          overdue: params.overdue ? "true" : undefined,
          bucket: params.bucket || undefined,
          search: params.search || undefined,
          sort_by: params.sortBy || undefined,
          sort_dir: params.sortDir || undefined,
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      })
      .then((r) => r.data as QuestionnairesPageDto)

    return {
      items: (data.items ?? []).map(mapListItemToRow),
      total: data.total ?? 0,
      page: data.page ?? 1,
      pageSize: data.pageSize ?? 25,
    }
  },

  getStats: async (): Promise<QuestionnaireStats> => {
    const d = await apiClientV2
      .get(`${QUESTIONNAIRES_BASE}/stats`)
      .then((r) => r.data as Partial<QuestionnaireStats>)
    return {
      received: d.received ?? 0,
      pending: d.pending ?? 0,
      stored: d.stored ?? 0,
      processed: d.processed ?? 0,
      extracting: d.extracting ?? 0,
      failed: d.failed ?? 0,
    }
  },

  /** Step 1 — multipart upload; backend runs AI detection and returns a draft. */
  uploadQuestionnaire: async (file: File): Promise<QuestionnaireDetailDto> => {
    const form = new FormData()
    form.append("file", file)
    return apiClientV2
      .post(`${QUESTIONNAIRES_BASE}/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data as QuestionnaireDetailDto)
  },

  getQuestionnaireDetail: async (
    id: string,
  ): Promise<QuestionnaireDetailDto> => {
    return apiClientV2
      .get(`${QUESTIONNAIRES_BASE}/${id}`)
      .then((r) => r.data as QuestionnaireDetailDto)
  },

  /**
   * Step 3 — confirm & store the request. With an upload draft we transition the
   * SAME row in place (`POST /{draftId}/store`) so no orphaned needs_confirm draft
   * is left behind; manual creation (no draft) still creates a fresh row.
   */
  storeQuestionnaire: async (
    input: StoreQuestionnaireInput,
  ): Promise<QuestionnaireListItemDto> => {
    const { draftId, ...payload } = input
    const url = draftId
      ? `${QUESTIONNAIRES_BASE}/${draftId}/store`
      : QUESTIONNAIRES_BASE
    return apiClientV2
      .post(url, payload)
      .then((r) => r.data as QuestionnaireListItemDto)
  },

  patchQuestionnaire: async (input: {
    id: string
    body: {
      customerId?: string
      customerDueDate?: string | null
      frameworks?: string[]
      regulationKeys?: string[]
      notes?: string | null
      status?: QuestionnaireStatus
    }
  }): Promise<QuestionnaireDetailDto> => {
    return apiClientV2
      .patch(`${QUESTIONNAIRES_BASE}/${input.id}`, input.body)
      .then((r) => r.data as QuestionnaireDetailDto)
  },

  deleteQuestionnaire: async (id: string): Promise<void> => {
    await apiClientV2.delete(`${QUESTIONNAIRES_BASE}/${id}`)
  },

  getFileDownload: async (
    id: string,
  ): Promise<{ url: string; fileName: string; expiresIn: number }> => {
    return apiClientV2
      .get(`${QUESTIONNAIRES_BASE}/${id}/file`)
      .then((r) => r.data)
  },

  /** Kicks off processing: status=stored_pending → processing_status=mapping. */
  startProcessing: async (id: string): Promise<void> => {
    await apiClientV2.post(`${QUESTIONNAIRES_BASE}/${id}/process`)
  },

  /** What an outgoing declaration answering this request would cover: the
   *  customer, the frameworks and the products the classifier already read out
   *  of the file. A prefill, not a draft — the reviewer confirms the scope. */
  getDeclarationTarget: async (id: string): Promise<DeclarationTarget> => {
    const response = await apiClientV2.get(
      `${QUESTIONNAIRES_BASE}/${id}/declaration-target`,
    )
    return response.data
  },

  listCustomers: async (search?: string): Promise<CustomerOut[]> => {
    return apiClientV2
      .get(CUSTOMERS_BASE, { params: { search: search || undefined } })
      .then((r) => (r.data as CustomerOut[]) ?? [])
  },

  createCustomer: async (body: {
    name: string
    email?: string | null
    declarationFreshnessDays?: number | null
    concentrationThresholdPct?: number | null
    preferredResponseFormat?: string | null
    additionalAttributes?: Record<string, unknown> | null
  }): Promise<CustomerOut> => {
    return apiClientV2.post(CUSTOMERS_BASE, body).then((r) => r.data)
  },
}

// ─── React Query hooks ────────────────────────────────────────────────────────

export function useQuestionnaires(params: QuestionnaireListParams) {
  return useQuery({
    queryKey: ["mc", "customer-requests", "list", params],
    queryFn: () => customerRequestsApiCalls.listQuestionnaires(params),
    placeholderData: keepPreviousData,
  })
}

export function useQuestionnaireStats() {
  return useQuery({
    queryKey: ["mc", "customer-requests", "stats"],
    queryFn: customerRequestsApiCalls.getStats,
  })
}

export function useUploadQuestionnaire() {
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.uploadQuestionnaire,
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useQuestionnaireIntake(id: string | null) {
  return useQuery({
    queryKey: ["mc", "customer-requests", "detail", id],
    queryFn: () =>
      customerRequestsApiCalls.getQuestionnaireDetail(id as string),
    enabled: !!id,
    select: mapDetailToIntake,
    // Async extraction: main-app publishes MC.Questionnaire.ExtractIntake on
    // upload and the extraction Lambda emits Extracted/ExtractFailed back into
    // the events poller — that flips status from `extracting` to `needs_confirm`
    // (or `extraction_failed`). Poll every 2.5s while still extracting; stop
    // once the terminal state lands. `select` runs after the raw fetch, so
    // check the DTO status here (pre-transform).
    refetchInterval: (query) => {
      const raw = query.state.data as QuestionnaireDetailDto | undefined
      return raw?.status === "extracting" ? 2500 : false
    },
  })
}

export function useQuestionnaire(id: string | null) {
  return useQuery({
    queryKey: ["mc", "customer-requests", "detail", id],
    queryFn: () =>
      customerRequestsApiCalls.getQuestionnaireDetail(id as string),
    enabled: !!id,
    select: mapDetailToRow,
  })
}

export function useStoreQuestionnaire() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.storeQuestionnaire,
    onSuccess: () => {
      // Prefix-invalidates the list, stats and detail caches.
      queryClient.invalidateQueries({ queryKey: ["mc", "customer-requests"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function usePatchQuestionnaire() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.patchQuestionnaire,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["mc", "customer-requests", "detail", variables.id],
      })
      queryClient.invalidateQueries({
        queryKey: ["mc", "customer-requests", "list"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useDeleteQuestionnaire() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.deleteQuestionnaire,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc", "customer-requests"] })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export interface DeclarationTarget {
  customerId: string
  documentType: string | null
  frameworks: string[]
  /** What the request asks, as keys: the conformity flow's preselection. */
  regulationKeys: string[]
  articleCodes: string[]
  products: {
    productId: string
    productNumber: string
    customerProductNumber: string | null
  }[]
  /** Codes that matched no product. Reported, never silently dropped: a short
   *  product list on a compliance statement is the worst outcome. */
  unmatchedCodes: string[]
}

export function useDeclarationTarget() {
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.getDeclarationTarget,
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_VIEW)
}

export function useQuestionnaireFileDownload() {
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.getFileDownload,
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_VIEW)
}

export function useStartProcessing() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.startProcessing,
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: ["mc", "customer-requests", "detail", id],
      })
      queryClient.invalidateQueries({
        queryKey: ["mc", "customer-requests", "list"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

// Namespaced under "customer-requests" (not "mc", "customers") — Master
// Data's customersApi.ts owns that key hierarchy for its own (paginated,
// heavier) customer list; sharing the prefix would make this module's
// invalidations also wipe Master Data's unrelated customer caches.
export function useCustomers(search?: string) {
  return useQuery({
    queryKey: ["mc", "customer-requests", "customers", search ?? ""],
    queryFn: () => customerRequestsApiCalls.listCustomers(search),
  })
}

export function useCreateCustomer() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: customerRequestsApiCalls.createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mc", "customer-requests", "customers"],
      })
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_CUSTOMERS_EDIT)
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function buildInitialEditable(
  intake: QuestionnaireIntake,
): QuestionnaireEditable {
  return {
    customer: intake.customer,
    customerId: intake.customerId,
    frameworks: intake.detected.frameworks.map((f) => f.name),
    regulationKeys: [...intake.regulationKeys],
    // `intake.due` (customerDueDate) is the backend's strict-parsed value —
    // the single source of truth. `intake.detected.dueDate` is the raw,
    // unvalidated LLM string (toDateOnly on it is a bare slice(0,10), not a
    // real-date check): a malformed extraction the backend correctly
    // rejected would otherwise still leak into this field. Never prefer it.
    due: intake.due,
    articleCodes: [...intake.detected.articleCodes],
    notes: "",
  }
}

/** Due-date urgency color. */
export function dueDateTone(due: string): "danger" | "warning" | "default" {
  if (!due) return "default"
  const now = new Date()
  const d = new Date(due)
  const days = Math.ceil((d.getTime() - now.getTime()) / 86_400_000)
  if (days <= 7) return "danger"
  if (days <= 14) return "warning"
  return "default"
}

/** Human-readable days-until label. */
export function daysUntilLabel(due: string, translate: TranslateFn): string {
  if (!due) return ""
  const days = Math.ceil((new Date(due).getTime() - Date.now()) / 86_400_000)
  if (days < 0) return translate("MC_Q_DUE_OVERDUE", { days: Math.abs(days) })
  if (days === 0) return translate("MC_Q_DUE_TODAY")
  return translate("MC_Q_DUE_LEFT", { days })
}
